import { createFileRoute, Outlet, redirect, useRouterState } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { lazy, Suspense, useEffect, useState } from "react";
import { PageDropZone } from "../components/upload/page-drop-zone";
import { UploadCenter } from "../components/upload/upload-modal";
import { UploadWatcher } from "../components/upload/upload-watcher";
import { isApiError, isTransientError } from "../lib/api";
import { useServerEvents } from "../lib/events";
import { meQuery } from "../lib/queries";
import { watchBodyLock } from "../lib/layers";
import { getTheme, setTheme } from "../lib/theme";
import { setNewDialogOpen, useUiState } from "../lib/ui-store";

const NewDialog = lazy(() => import("../components/upload/new-dialog").then((m) => ({ default: m.NewDialog })));

/** Every page below needs a signed-in person. Not signed in → /signin?next=… */
export const Route = createFileRoute("/_authed")({
  beforeLoad: async ({ context, location }) => {
    try {
      const me = await context.queryClient.ensureQueryData(meQuery);
      return { me };
    } catch (e) {
      if (isApiError(e) && e.status === 401) {
        throw redirect({ to: "/signin", search: { next: location.href } });
      }
      // Carno briefly unreachable: render the page anyway; the banner says
      // "Reconnecting…" and queries retry until it's back.
      if (isTransientError(e)) return { me: null };
      throw e;
    }
  },
  component: AuthedLayout,
});

function AuthedLayout() {
  useServerEvents();
  const me = useQuery(meQuery);
  const { newDialog } = useUiState();
  const [newDialogUsed, setNewDialogUsed] = useState(false);
  if (newDialog.open && !newDialogUsed) setNewDialogUsed(true);
  useEffect(() => watchBodyLock(), []);
  // Drag-to-upload anywhere on Home and the App view (the New dialog has its own drop area).
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const dropEnabled = !newDialog.open && (pathname === "/" || /^\/a\/[^/]+\/?$/.test(pathname));

  // The server's theme preference wins once it's known.
  const serverTheme = me.data?.prefs.theme;
  useEffect(() => {
    if (serverTheme !== undefined && serverTheme !== getTheme()) setTheme(serverTheme);
  }, [serverTheme]);

  return (
    <>
      <Outlet />
      <UploadWatcher />
      <UploadCenter />
      <PageDropZone enabled={dropEnabled} />
      {/* Stay mounted once opened: closing goes through `open`, so Radix cleans up normally. */}
      {newDialogUsed && (
        <Suspense fallback={null}>
          <NewDialog open={newDialog.open} onOpenChange={setNewDialogOpen} />
        </Suspense>
      )}
    </>
  );
}
